import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MaterialModule } from '../../material/material.module';
import { DataService } from '../../middle-school-math/services/data';
import { Book, CurriculumManifest, Subject } from '../../middle-school-math/models';
import { SubscriptionPaywallDialogComponent } from '../../shared/payment/subscription-paywall-dialog.component';

@Component({
  selector: 'app-courses-catalog',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, MaterialModule],
  template: `
    <div class="catalog-page">
      <!-- HERO HEADER -->
      <section class="hero-section">
        <div class="hero-glow hero-glow-1"></div>
        <div class="hero-glow hero-glow-2"></div>
        <div class="hero-content">
          <div class="hero-badge">
            <mat-icon>school</mat-icon>
            <span>{{ pageConfig?.heroBadge || 'Interactive Academics & Digital Textbooks' }}</span>
          </div>
          <h1 class="hero-title">
            <span>{{ heroTitleBefore }}</span>
            <span class="gradient-text" *ngIf="heroTitleGradient">{{ heroTitleGradient }}</span>
            <span *ngIf="heroTitleAfter">{{ heroTitleAfter }}</span>
          </h1>
          <p class="hero-subtitle">
            {{ pageConfig?.heroSubtitle || 'Master Pre-Algebra, Physics, Computer Science, and more with animated lesson pages, crystal-clear audio narrations, spaced-repetition flashcards, and instant-graded quizzes.' }}
          </p>

          <!-- VALUE PERKS ROW -->
          <div class="perks-row" *ngIf="pageConfig?.perks?.length">
            <div class="perk-chip" *ngFor="let perk of pageConfig.perks">
              <mat-icon>{{ perk.icon || 'star' }}</mat-icon>
              <span>{{ perk.text }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- CATALOG CONTROLS & FILTER BAR -->
      <div class="catalog-container">
        <div class="filters-bar">
          <!-- Subject Tabs -->
          <div class="subject-tabs">
            <button
              type="button"
              class="subject-tab"
              [class.active]="selectedSubjectId === 'all'"
              (click)="selectSubject('all')">
              <mat-icon>apps</mat-icon>
              <span>All Courses ({{ books.length }})</span>
            </button>
            <button
              *ngFor="let s of subjects"
              type="button"
              class="subject-tab"
              [class.active]="selectedSubjectId === s.id"
              [style.--subj-color]="s.color || '#1976d2'"
              (click)="selectSubject(s.id)">
              <mat-icon>{{ s.icon || 'menu_book' }}</mat-icon>
              <span>{{ s.name }} ({{ getBookCountBySubject(s.id) }})</span>
            </button>
          </div>

          <!-- Search Box -->
          <div class="search-box">
            <mat-form-field appearance="outline" class="search-input">
              <mat-icon matPrefix>search</mat-icon>
              <input
                matInput
                [(ngModel)]="searchQuery"
                (ngModelChange)="filterBooks()"
                placeholder="Search courses or topics..." />
              <button
                mat-icon-button
                matSuffix
                *ngIf="searchQuery"
                (click)="searchQuery = ''; filterBooks()"
                aria-label="Clear search">
                <mat-icon>clear</mat-icon>
              </button>
            </mat-form-field>
          </div>
        </div>

        <!-- LOADING STATE -->
        <div *ngIf="loading" class="loading-state">
          <mat-spinner diameter="44"></mat-spinner>
          <p>Loading course catalog...</p>
        </div>

        <!-- EMPTY STATE -->
        <div *ngIf="!loading && filteredBooks.length === 0" class="empty-state">
          <mat-icon>search_off</mat-icon>
          <h3>No courses found</h3>
          <p>Try clearing your search query or selecting a different subject filter.</p>
          <button mat-flat-button color="primary" (click)="resetFilters()">Reset Filters</button>
        </div>

        <!-- BOOKS GRID -->
        <div *ngIf="!loading && filteredBooks.length > 0" class="books-grid">
          <div *ngFor="let book of filteredBooks" class="book-card" [class.subscribed-card]="isBookSubscribed(book.id)">
            <!-- Top Subject Ribbon & Status Pill -->
            <div class="book-card-header">
              <div class="subject-tag" [style.background]="getSubjectColor(book.subjectId)">
                <mat-icon class="subj-tag-icon">{{ getSubjectIcon(book.subjectId) }}</mat-icon>
                <span>{{ getSubjectName(book.subjectId) }}</span>
              </div>

              <!-- Pill: Subscribed vs Paid vs Free -->
              <div class="pricing-pill" 
                   [class.sub-active]="book.isSubscriptionEnabled && isBookSubscribed(book.id)" 
                   [class.sub-enabled]="book.isSubscriptionEnabled && !isBookSubscribed(book.id)">
                <mat-icon>{{ isBookSubscribed(book.id) ? 'verified' : (book.isSubscriptionEnabled ? 'workspace_premium' : 'lock_open') }}</mat-icon>
                <span>
                  {{ isBookSubscribed(book.id) ? 'Subscribed' : (book.isSubscriptionEnabled ? ('$' + (book.monthlyPrice || 9.99) + '/mo') : 'Free Course') }}
                </span>
              </div>
            </div>

            <!-- Card Body -->
            <div class="book-card-body">
              <div class="book-avatar" [style.background]="getSubjectColor(book.subjectId) + '15'" [style.color]="getSubjectColor(book.subjectId)">
                <mat-icon>{{ book.icon || 'auto_stories' }}</mat-icon>
              </div>

              <h2 class="book-title">{{ book.title }}</h2>
              <p class="book-desc">{{ book.description || 'Comprehensive interactive textbook curriculum designed for mastery and retention.' }}</p>

              <!-- Features checklist -->
              <div class="book-features">
                <div class="feature-item">
                  <mat-icon class="feature-icon check">check_circle</mat-icon>
                  <span>Interactive lesson pages with audio</span>
                </div>
                <div class="feature-item">
                  <mat-icon class="feature-icon check">check_circle</mat-icon>
                  <span>Active recall flashcard decks</span>
                </div>
                <div class="feature-item">
                  <mat-icon class="feature-icon check">check_circle</mat-icon>
                  <span>Self-paced mastery quizzes</span>
                </div>

                <!-- Feature Pill: Subscribed vs Preview vs Free -->
                <div class="feature-item active-sub-feature" *ngIf="book.isSubscriptionEnabled && isBookSubscribed(book.id)">
                  <mat-icon class="feature-icon active">verified</mat-icon>
                  <strong>Full Course Access Unlocked</strong>
                </div>
                <div class="feature-item free-preview-feature" *ngIf="book.isSubscriptionEnabled && !isBookSubscribed(book.id)">
                  <mat-icon class="feature-icon preview">visibility</mat-icon>
                  <strong>Free Preview Chapters Available</strong>
                </div>
                <div class="feature-item free-preview-feature" *ngIf="!book.isSubscriptionEnabled">
                  <mat-icon class="feature-icon free">lock_open</mat-icon>
                  <strong>All Chapters 100% Free</strong>
                </div>
              </div>

              <!-- Pricing / Access Box -->
              <!-- Case 1: Active Subscriber -->
              <div class="pricing-box active-box" *ngIf="book.isSubscriptionEnabled && isBookSubscribed(book.id)">
                <div class="active-sub-msg">
                  <mat-icon class="active-sub-icon">verified</mat-icon>
                  <div>
                    <div class="active-sub-title">Active Subscriber Access</div>
                    <p class="active-sub-desc">You have unrestricted access to all chapters and quizzes.</p>
                  </div>
                </div>
              </div>

              <!-- Case 2: Subscription Enabled (Not Subscribed) -->
              <div class="pricing-box" *ngIf="book.isSubscriptionEnabled && !isBookSubscribed(book.id)">
                <div class="pricing-tier-header">
                  <span class="sub-label">Subscription Pricing</span>
                  <span class="save-badge">Save ~33% Annual</span>
                </div>
                <div class="pricing-numbers">
                  <div class="price-col">
                    <span class="price-val">\${{ book.monthlyPrice || 9.99 }}</span>
                    <span class="price-period">/ month</span>
                  </div>
                  <span class="price-divider">or</span>
                  <div class="price-col">
                    <span class="price-val">\${{ book.annualPrice || 79.99 }}</span>
                    <span class="price-period">/ year</span>
                  </div>
                </div>
              </div>

              <!-- Case 3: Free Book -->
              <div class="pricing-box free-box" *ngIf="!book.isSubscriptionEnabled">
                <div class="free-text">
                  <mat-icon>celebration</mat-icon>
                  <span>100% Free Open Access Curriculum</span>
                </div>
              </div>
            </div>

            <!-- Card Actions Footer -->
            <div class="book-card-footer">
              <!-- Subscribed: Single Primary "Access Course" Button -->
              <button
                *ngIf="book.isSubscriptionEnabled && isBookSubscribed(book.id)"
                mat-flat-button
                class="access-course-btn"
                (click)="openBook(book)">
                <mat-icon>play_circle</mat-icon>
                <span>Access Course &rarr;</span>
              </button>

              <!-- Not Subscribed Paid Book: Preview & Subscribe Buttons -->
              <button
                *ngIf="book.isSubscriptionEnabled && !isBookSubscribed(book.id)"
                mat-flat-button
                class="explore-btn"
                (click)="openBook(book)">
                <mat-icon>visibility</mat-icon>
                <span>Preview Free Chapters</span>
              </button>

              <button
                *ngIf="book.isSubscriptionEnabled && !isBookSubscribed(book.id)"
                mat-stroked-button
                class="subscribe-btn"
                (click)="openSubscribeModal(book)">
                <mat-icon>card_membership</mat-icon>
                <span>Subscribe Now</span>
              </button>

              <!-- Free Book: Start Free Course -->
              <button
                *ngIf="!book.isSubscriptionEnabled"
                mat-flat-button
                class="explore-btn"
                (click)="openBook(book)">
                <mat-icon>play_circle</mat-icon>
                <span>Start Free Course</span>
              </button>
            </div>
          </div>
        </div>

        <!-- WHY CHOOSE KSCOURSES BANNER -->
        <section class="why-section">
          <div class="why-header">
            <span class="section-tag">{{ pageConfig?.whySectionTag || 'THE KSCOURSES ADVANTAGE' }}</span>
            <h2>{{ pageConfig?.whySectionTitle || 'Why Students & Parents Love Our Interactive Books' }}</h2>
            <p>{{ pageConfig?.whySectionSubtitle || 'Built for clarity, speed, and real mastery. No boring PDFs—just high-engagement learning.' }}</p>
          </div>

          <div class="why-grid" *ngIf="pageConfig?.whyCards?.length">
            <div *ngFor="let card of pageConfig.whyCards" class="why-card">
              <div class="why-icon-box" [style.background]="(card.color || '#1976d2') + '18'" [style.color]="card.color || '#1976d2'">
                <mat-icon>{{ card.icon || 'star' }}</mat-icon>
              </div>
              <h3>{{ card.title }}</h3>
              <p>{{ card.description }}</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  `,
  styles: [`
    .catalog-page {
      min-height: calc(100vh - 64px);
      background: #f8fafc;
      color: #0f172a;
      padding-bottom: 5rem;
    }

    /* HERO SECTION */
    .hero-section {
      position: relative;
      background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
      color: white;
      padding: 4.5rem 1.5rem 4rem;
      text-align: center;
      overflow: hidden;
    }
    .hero-glow {
      position: absolute;
      border-radius: 50%;
      filter: blur(100px);
      pointer-events: none;
      opacity: 0.35;
    }
    .hero-glow-1 {
      width: 400px;
      height: 400px;
      background: #3b82f6;
      top: -100px;
      left: 10%;
    }
    .hero-glow-2 {
      width: 350px;
      height: 350px;
      background: #8b5cf6;
      bottom: -100px;
      right: 10%;
    }
    .hero-content {
      position: relative;
      z-index: 1;
      max-width: 900px;
      margin: 0 auto;
    }
    .hero-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.15);
      backdrop-filter: blur(8px);
      padding: 0.4rem 1rem;
      border-radius: 9999px;
      font-size: 0.875rem;
      font-weight: 600;
      color: #93c5fd;
      margin-bottom: 1.25rem;
    }
    .hero-badge mat-icon {
      font-size: 1.1rem;
      height: 1.1rem;
      width: 1.1rem;
    }
    .hero-title {
      font-size: 3rem;
      font-weight: 800;
      line-height: 1.15;
      margin-bottom: 1rem;
      letter-spacing: -0.02em;
    }
    .gradient-text {
      background: linear-gradient(135deg, #60a5fa 0%, #a78bfa 50%, #f472b6 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    .hero-subtitle {
      font-size: 1.125rem;
      line-height: 1.6;
      color: #cbd5e1;
      max-width: 750px;
      margin: 0 auto 2rem;
    }
    .perks-row {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
      gap: 0.75rem;
    }
    .perk-chip {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.12);
      padding: 0.4rem 0.85rem;
      border-radius: 8px;
      font-size: 0.85rem;
      font-weight: 500;
      color: #e2e8f0;
    }
    .perk-chip mat-icon {
      font-size: 1rem;
      height: 1rem;
      width: 1rem;
      color: #38bdf8;
    }

    /* CATALOG CONTAINER */
    .catalog-container {
      max-width: 1200px;
      margin: -2rem auto 0;
      padding: 0 1.5rem;
      position: relative;
      z-index: 2;
    }

    /* FILTERS BAR */
    .filters-bar {
      background: white;
      border-radius: 16px;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.06), 0 8px 10px -6px rgba(0, 0, 0, 0.04);
      padding: 1.25rem 1.5rem;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
      margin-bottom: 2.5rem;
      border: 1px solid #e2e8f0;
    }
    .subject-tabs {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }
    .subject-tab {
      display: flex;
      align-items: center;
      gap: 0.45rem;
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
      color: #475569;
      padding: 0.55rem 1.1rem;
      border-radius: 9999px;
      font-size: 0.875rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    .subject-tab mat-icon {
      font-size: 1.15rem;
      height: 1.15rem;
      width: 1.15rem;
    }
    .subject-tab:hover {
      background: #e2e8f0;
      color: #0f172a;
    }
    .subject-tab.active {
      background: var(--subj-color, #1976d2);
      border-color: var(--subj-color, #1976d2);
      color: white;
      box-shadow: 0 4px 12px rgba(25, 118, 210, 0.25);
    }
    .search-box {
      flex: 1;
      min-width: 260px;
      max-width: 360px;
    }
    .search-input {
      width: 100%;
      margin-bottom: -1.25em;
    }

    /* STATES */
    .loading-state, .empty-state {
      text-align: center;
      padding: 4rem 2rem;
      background: white;
      border-radius: 16px;
      border: 1px solid #e2e8f0;
      color: #64748b;
    }
    .loading-state p {
      margin-top: 1rem;
      font-weight: 500;
    }
    .empty-state mat-icon {
      font-size: 56px;
      height: 56px;
      width: 56px;
      margin-bottom: 0.5rem;
      color: #94a3b8;
    }
    .empty-state h3 {
      font-size: 1.25rem;
      color: #1e293b;
      margin: 0.5rem 0;
    }
    .empty-state p {
      margin-bottom: 1.5rem;
    }

    /* BOOKS GRID */
    .books-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
      gap: 2rem;
      margin-bottom: 4rem;
    }
    .book-card {
      background: white;
      border-radius: 18px;
      border: 1px solid #e2e8f0;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
    }
    .book-card:hover {
      transform: translateY(-6px);
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04);
      border-color: #cbd5e1;
    }
    .book-card.subscribed-card {
      border-color: #86efac;
      box-shadow: 0 4px 14px rgba(34, 197, 94, 0.08);
    }
    .book-card.subscribed-card:hover {
      border-color: #4ade80;
      box-shadow: 0 20px 25px -5px rgba(34, 197, 94, 0.15);
    }

    .book-card-header {
      padding: 1.25rem 1.5rem 0.5rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .subject-tag {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      color: white;
      padding: 0.3rem 0.75rem;
      border-radius: 9999px;
      font-size: 0.75rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }
    .subj-tag-icon {
      font-size: 0.9rem;
      height: 0.9rem;
      width: 0.9rem;
    }
    .pricing-pill {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      background: #f1f5f9;
      color: #475569;
      padding: 0.3rem 0.75rem;
      border-radius: 9999px;
      font-size: 0.75rem;
      font-weight: 700;
    }
    .pricing-pill mat-icon {
      font-size: 0.9rem;
      height: 0.9rem;
      width: 0.9rem;
    }
    .pricing-pill.sub-enabled {
      background: #fef3c7;
      color: #92400e;
      border: 1px solid #fde68a;
    }
    .pricing-pill.sub-active {
      background: #dcfce7;
      color: #166534;
      border: 1px solid #bbf7d0;
      font-weight: 800;
    }
    .pricing-pill.sub-active mat-icon {
      color: #16a34a;
    }

    .book-card-body {
      padding: 1rem 1.5rem 1.5rem;
      flex: 1;
      display: flex;
      flex-direction: column;
    }
    .book-avatar {
      width: 54px;
      height: 54px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 1rem;
    }
    .book-avatar mat-icon {
      font-size: 30px;
      height: 30px;
      width: 30px;
    }
    .book-title {
      font-size: 1.35rem;
      font-weight: 700;
      color: #0f172a;
      margin: 0 0 0.5rem;
      line-height: 1.3;
    }
    .book-desc {
      font-size: 0.9rem;
      line-height: 1.55;
      color: #64748b;
      margin: 0 0 1.25rem;
      flex: 1;
    }

    .book-features {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin-bottom: 1.25rem;
      padding-top: 1rem;
      border-top: 1px solid #f1f5f9;
    }
    .feature-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.85rem;
      color: #475569;
    }
    .feature-icon.check {
      color: #16a34a;
      font-size: 1rem;
      height: 1rem;
      width: 1rem;
    }
    .feature-icon.preview {
      color: #2563eb;
      font-size: 1rem;
      height: 1rem;
      width: 1rem;
    }
    .feature-icon.active {
      color: #16a34a;
      font-size: 1rem;
      height: 1rem;
      width: 1rem;
    }
    .free-preview-feature {
      color: #1e40af;
      font-weight: 600;
      background: #eff6ff;
      padding: 0.35rem 0.65rem;
      border-radius: 6px;
    }
    .active-sub-feature {
      color: #15803d;
      font-weight: 700;
      background: #f0fdf4;
      padding: 0.35rem 0.65rem;
      border-radius: 6px;
      border: 1px solid #dcfce7;
    }

    .pricing-box {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 0.85rem 1rem;
      margin-top: auto;
    }
    .pricing-tier-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.35rem;
    }
    .sub-label {
      font-size: 0.75rem;
      font-weight: 700;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .save-badge {
      background: #dcfce7;
      color: #15803d;
      font-size: 0.7rem;
      font-weight: 700;
      padding: 0.15rem 0.45rem;
      border-radius: 4px;
    }
    .pricing-numbers {
      display: flex;
      align-items: baseline;
      gap: 0.5rem;
    }
    .price-col {
      display: flex;
      align-items: baseline;
      gap: 0.2rem;
    }
    .price-val {
      font-size: 1.25rem;
      font-weight: 800;
      color: #0f172a;
    }
    .price-period {
      font-size: 0.75rem;
      color: #64748b;
      font-weight: 600;
    }
    .price-divider {
      font-size: 0.75rem;
      color: #94a3b8;
    }
    .free-box {
      background: #f0fdf4;
      border-color: #bbf7d0;
    }
    .free-text {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #166534;
      font-weight: 600;
      font-size: 0.85rem;
    }
    .free-text mat-icon {
      font-size: 1.1rem;
      height: 1.1rem;
      width: 1.1rem;
    }

    .active-box {
      background: #f0fdf4;
      border: 1.5px solid #86efac;
    }
    .active-sub-msg {
      display: flex;
      align-items: flex-start;
      gap: 0.65rem;
    }
    .active-sub-icon {
      color: #16a34a;
      font-size: 22px;
      height: 22px;
      width: 22px;
      flex-shrink: 0;
      margin-top: 2px;
    }
    .active-sub-title {
      font-size: 0.9rem;
      font-weight: 700;
      color: #15803d;
      margin-bottom: 2px;
    }
    .active-sub-desc {
      font-size: 0.8rem;
      color: #4b5563;
      margin: 0;
      line-height: 1.4;
    }

    .book-card-footer {
      padding: 1.25rem 1.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.6rem;
      background: #fafafa;
      border-top: 1px solid #f1f5f9;
    }
    .explore-btn {
      width: 100%;
      background: #1976d2 !important;
      color: white !important;
      font-weight: 600;
      border-radius: 8px;
      padding: 0.55rem 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .access-course-btn {
      width: 100%;
      background: linear-gradient(135deg, #16a34a 0%, #15803d 100%) !important;
      color: white !important;
      font-weight: 700;
      border-radius: 8px;
      padding: 0.65rem 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1rem;
      box-shadow: 0 4px 10px rgba(22, 163, 74, 0.25);
      transition: all 0.2s ease;
    }
    .access-course-btn:hover {
      background: linear-gradient(135deg, #15803d 0%, #166534 100%) !important;
      transform: translateY(-1px);
      box-shadow: 0 6px 14px rgba(22, 163, 74, 0.35);
    }
    .subscribe-btn {
      width: 100%;
      color: #d97706 !important;
      border: 1.5px solid #f59e0b !important;
      background: #fffbeb !important;
      font-weight: 700;
      border-radius: 8px;
      padding: 0.55rem 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .explore-btn mat-icon, .access-course-btn mat-icon, .subscribe-btn mat-icon {
      margin-right: 0.4rem;
      font-size: 1.15rem;
      height: 1.15rem;
      width: 1.15rem;
    }

    /* WHY SECTION */
    .why-section {
      background: white;
      border-radius: 24px;
      border: 1px solid #e2e8f0;
      padding: 4rem 2.5rem;
      box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.05);
    }
    .why-header {
      text-align: center;
      max-width: 700px;
      margin: 0 auto 3rem;
    }
    .section-tag {
      font-size: 0.8rem;
      font-weight: 800;
      color: #2563eb;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      display: block;
      margin-bottom: 0.5rem;
    }
    .why-header h2 {
      font-size: 2.25rem;
      font-weight: 800;
      color: #0f172a;
      margin: 0 0 0.75rem;
      letter-spacing: -0.02em;
    }
    .why-header p {
      color: #64748b;
      font-size: 1.05rem;
      line-height: 1.6;
      margin: 0;
    }
    .why-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
      gap: 2rem;
    }
    .why-card {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 16px;
      padding: 2rem 1.5rem;
      text-align: center;
      transition: transform 0.2s ease, border-color 0.2s ease;
    }
    .why-card:hover {
      transform: translateY(-4px);
      border-color: #cbd5e1;
    }
    .why-icon-box {
      width: 56px;
      height: 56px;
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1.25rem;
    }
    .why-icon-box mat-icon {
      font-size: 30px;
      height: 30px;
      width: 30px;
    }
    .why-card h3 {
      font-size: 1.15rem;
      font-weight: 700;
      color: #0f172a;
      margin: 0 0 0.5rem;
    }
    .why-card p {
      font-size: 0.875rem;
      line-height: 1.55;
      color: #64748b;
      margin: 0;
    }

    @media (max-width: 768px) {
      .hero-title {
        font-size: 2.25rem;
      }
      .filters-bar {
        flex-direction: column;
        align-items: stretch;
      }
      .search-box {
        max-width: 100%;
      }
      .books-grid {
        grid-template-columns: 1fr;
      }
      .why-section {
        padding: 2.5rem 1.5rem;
      }
    }
  `]
})
export class CoursesCatalogComponent implements OnInit {
  private dataService = inject(DataService);
  private dialog = inject(MatDialog);
  private router = inject(Router);

  subjects: Subject[] = [];
  books: Book[] = [];
  filteredBooks: Book[] = [];
  activeSubscribedBookIds: Set<string> = new Set<string>();

  selectedSubjectId: string = 'all';
  searchQuery: string = '';
  loading = true;

  pageConfig: any = {
    heroBadge: 'Interactive Academics & Digital Textbooks',
    heroTitle: 'Explore Courses & Interactive Books',
    heroGradientText: 'Interactive Books',
    heroSubtitle: 'Master Pre-Algebra, Physics, Computer Science, and more with animated lesson pages, crystal-clear audio narrations, spaced-repetition flashcards, and instant-graded quizzes.',
    perks: [
      { icon: 'lock_open', text: 'Free Preview on All Books' },
      { icon: 'headset', text: 'Audio-Guided Lessons' },
      { icon: 'style', text: 'Interactive Flashcards' },
      { icon: 'verified', text: 'Instant Quiz Grading' }
    ],
    whySectionTag: 'THE KSCOURSES ADVANTAGE',
    whySectionTitle: 'Why Students & Parents Love Our Interactive Books',
    whySectionSubtitle: 'Built for clarity, speed, and real mastery. No boring PDFs—just high-engagement learning.',
    whyCards: [
      {
        icon: 'slideshow',
        color: '#1976d2',
        title: 'Visual Page-by-Page Lessons',
        description: 'Step-by-step illustrated concept pages break down complex mathematical and scientific theories into digestible steps.'
      },
      {
        icon: 'record_voice_over',
        color: '#00897b',
        title: 'Professional Audio Narration',
        description: 'Every page includes synchronized audio playback so auditory and visual learners can follow along effortlessly.'
      },
      {
        icon: 'style',
        color: '#e65100',
        title: 'Active Recall Flashcards',
        description: 'Reinforce essential formulas, definitions, and key problem types with flip cards designed for long-term memory.'
      },
      {
        icon: 'quiz',
        color: '#6a1b9a',
        title: 'Adaptive Practice Quizzes',
        description: 'Test knowledge with multiple-choice quizzes that provide immediate scoring, explanations, and mastery tracking.'
      }
    ]
  };

  get heroTitleBefore(): string {
    if (!this.pageConfig?.heroTitle) return 'Explore Courses & ';
    if (!this.pageConfig.heroGradientText) return this.pageConfig.heroTitle;
    const idx = this.pageConfig.heroTitle.indexOf(this.pageConfig.heroGradientText);
    if (idx < 0) return this.pageConfig.heroTitle;
    return this.pageConfig.heroTitle.substring(0, idx);
  }

  get heroTitleGradient(): string {
    if (!this.pageConfig?.heroGradientText) return '';
    if (!this.pageConfig?.heroTitle?.includes(this.pageConfig.heroGradientText)) return '';
    return this.pageConfig.heroGradientText;
  }

  get heroTitleAfter(): string {
    if (!this.pageConfig?.heroTitle || !this.pageConfig?.heroGradientText) return '';
    const idx = this.pageConfig.heroTitle.indexOf(this.pageConfig.heroGradientText);
    if (idx < 0) return '';
    return this.pageConfig.heroTitle.substring(idx + this.pageConfig.heroGradientText.length);
  }

  ngOnInit(): void {
    this.loadCatalog();
    this.loadUserSubscriptions();
  }

  loadCatalog(): void {
    this.loading = true;
    this.dataService.getCurriculum().subscribe({
      next: (manifest: CurriculumManifest) => {
        this.subjects = manifest.subjects || [];
        this.books = manifest.books || [];
        this.filterBooks();
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load curriculum:', err);
        this.loading = false;
      }
    });

    this.dataService.getCoursesPageConfig().subscribe({
      next: (config) => {
        if (config && config.heroTitle) {
          this.pageConfig = config;
        }
      },
      error: (err) => {
        console.warn('Could not load courses page config:', err);
      }
    });
  }

  loadUserSubscriptions(): void {
    const userEmail = this.dataService.getCurrentUserEmail();
    if (!userEmail) {
      this.activeSubscribedBookIds.clear();
      return;
    }

    this.dataService.getMySubscriptions().subscribe({
      next: (subs) => {
        this.activeSubscribedBookIds.clear();
        if (subs && Array.isArray(subs)) {
          const now = new Date();
          subs.forEach((sub: any) => {
            const isPeriodValid = !sub.currentPeriodEnd || new Date(sub.currentPeriodEnd) > now;
            if (sub.status === 'ACTIVE' && isPeriodValid && sub.bookId) {
              this.activeSubscribedBookIds.add(sub.bookId);
              // Support alternate key formats
              this.activeSubscribedBookIds.add(sub.bookId.replace(/-/g, '_'));
              this.activeSubscribedBookIds.add(sub.bookId.replace(/_/g, '-'));
            }
          });
        }
      },
      error: (err) => {
        this.activeSubscribedBookIds.clear();
        console.warn('Could not fetch user subscriptions on courses catalog:', err);
      }
    });
  }

  isBookSubscribed(bookId: string): boolean {
    if (!bookId) return false;
    return this.activeSubscribedBookIds.has(bookId) ||
           this.activeSubscribedBookIds.has(bookId.replace(/-/g, '_')) ||
           this.activeSubscribedBookIds.has(bookId.replace(/_/g, '-'));
  }

  selectSubject(subjectId: string): void {
    this.selectedSubjectId = subjectId;
    this.filterBooks();
  }

  filterBooks(): void {
    let list = this.books;

    if (this.selectedSubjectId !== 'all') {
      list = list.filter(b => b.subjectId === this.selectedSubjectId);
    }

    if (this.searchQuery && this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      list = list.filter(b =>
        (b.title && b.title.toLowerCase().includes(q)) ||
        (b.description && b.description.toLowerCase().includes(q)) ||
        (b.id && b.id.toLowerCase().includes(q))
      );
    }

    this.filteredBooks = list;
  }

  resetFilters(): void {
    this.selectedSubjectId = 'all';
    this.searchQuery = '';
    this.filterBooks();
  }

  getBookCountBySubject(subjectId: string): number {
    return this.books.filter(b => b.subjectId === subjectId).length;
  }

  getSubjectName(subjectId: string): string {
    const s = this.subjects.find(sub => sub.id === subjectId);
    return s ? s.name : subjectId;
  }

  getSubjectColor(subjectId: string): string {
    const s = this.subjects.find(sub => sub.id === subjectId);
    return s?.color || '#1976d2';
  }

  getSubjectIcon(subjectId: string): string {
    const s = this.subjects.find(sub => sub.id === subjectId);
    return s?.icon || 'menu_book';
  }

  openBook(book: Book): void {
    // Navigate to course landing page (e.g. /middle-school-math or /middle_school_math)
    const route = book.id === 'middle_school_math' ? '/middle-school-math' : `/${book.id}`;
    this.router.navigate([route]);
  }

  openSubscribeModal(book: Book): void {
    const dialogRef = this.dialog.open(SubscriptionPaywallDialogComponent, {
      width: '650px',
      maxWidth: '95vw',
      panelClass: 'subscription-modal-panel',
      data: {
        book: book,
        reason: 'Unlock all chapters, lesson pages, and quizzes in ' + book.title
      }
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res && res.purchased) {
        this.loadUserSubscriptions();
        this.openBook(book);
      }
    });
  }
}
